import { useCallback, useRef, useState } from "react";

export interface SingleFlight {
	/** Runs the action unless one is already running; a call made meanwhile is dropped. */
	run: (action: () => Promise<unknown> | unknown) => Promise<void>;
	/** Whether an action is running, for the control that starts it to show it is busy. */
	isRunning: boolean;
}

/**
 * Runs an action at most once at a time. A confirm button that is clicked twice otherwise starts
 * its action twice before the button can render as disabled, and a server that checks for a
 * duplicate by reading before it writes then writes the row twice: a double click on "To
 * Blacklist" wrote the company entry twice (PREC-4425).
 */
export function useSingleFlight(): SingleFlight {
	const runningRef = useRef(false);
	const [isRunning, setIsRunning] = useState(false);

	const run = useCallback(async (action: () => Promise<unknown> | unknown) => {
		if (runningRef.current) {
			return;
		}

		runningRef.current = true;
		setIsRunning(true);

		try {
			await action();
		} finally {
			runningRef.current = false;
			setIsRunning(false);
		}
	}, []);

	return { run, isRunning };
}
